import React, { useEffect, useState } from 'react';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import {
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@material-ui/core';
import { IPasswordPolicy } from '../../components/UserProfile/IUserProfile.types';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { getPasswordPolicy } from '../../services/users/users.service';
import { useStyles } from '../../components/UserProfile/EditUserPassword.styles';
import { AuthUserPesel } from '../addUser/AuthUserPesel.component';
import {
  BackendErrorCode,
  FrontendResponseErrorCode,
  ErrorCode,
} from '../../components/UserProfile/PasswordError.types';
import { AxiosResponse } from 'axios';
import { getPasswordPolicyMessage, validatePasswordWithPolicy } from '../../components/UserProfile/helpers';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { getErrorMessages } from '../UserProfile/helpers';
import { useTranslation } from 'react-i18next';

interface AddUserPasswordProps {
  open: boolean;
  token: string;
  email: string;
  onClose: () => void;
}
export const AddUserPassword: React.FC<AddUserPasswordProps> = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const [newPassword, setNewPassword] = useState<string>('');
  const [repeatedPassword, setRepeatedPassword] = useState<string>('');
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState<boolean>(false);
  const [errorCodes, setErrorCodes] = useState<ErrorCode[]>([]);
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [passwordPolicy, setPasswordPolicy] = useState<IPasswordPolicy | undefined>(undefined);

  const [openAuthUserPesel, setOpenAuthUserPesel] = useState<boolean>(false);

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowRepeatedPassword = () => {
    setShowRepeatedPassword(!showRepeatedPassword);
  };

  const handleChangeNewPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
    setErrorCodes([]);
  };

  const handleChangeRepeatedPassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeatedPassword(event.target.value);
    setErrorCodes([]);
  };

  const clearInputs = () => {
    setNewPassword('');
    setRepeatedPassword('');
    setShowNewPassword(false);
    setShowRepeatedPassword(false);
    setErrorCodes([]);
  };

  const handleOpenAuthPesel = () => {
    setOpenAuthUserPesel(true);
  };

  const handleCloseAuthPesel = () => {
    setOpenAuthUserPesel(false);
  };

  const handleConfirmAccount = () => {
    setOpenAuthUserPesel(false);
    setTimeout(() => {
      history.push('/');
    }, 5000);
  };

  useEffect(() => {
    setIsDataChanged(newPassword !== '' && repeatedPassword !== '');
    if (newPassword !== '' && repeatedPassword !== '' && newPassword !== repeatedPassword) {
      setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordsNotTheSame]));
    }
    if (passwordPolicy && newPassword !== '' && repeatedPassword !== '' && newPassword === repeatedPassword) {
      if (!validatePasswordWithPolicy(newPassword, passwordPolicy)) {
        setErrorCodes((oldArray: ErrorCode[]) => _.uniq([...oldArray, FrontendResponseErrorCode.PasswordIsInvalid]));
      }
    }
  }, [newPassword, repeatedPassword, passwordPolicy]);

  useEffect(() => {
    setIsUpdateButtonDisabled(!isDataChanged || errorCodes.length > 0);
  }, [isDataChanged, errorCodes, errorCodes.length]);

  const onCancelEdit = () => {
    clearInputs();
    props.onClose();
  };

  const invalidPassword = !errorCodes.includes(BackendErrorCode.PasswordMismatch) && errorCodes.length > 0;

  useEffect(() => {
    getPasswordPolicy().then((response: AxiosResponse<IPasswordPolicy>) => {
      setPasswordPolicy(response.data);
    });
  }, []);

  return (
    <>
      <ModalWithButtons
        open={props.open}
        size="sm"
        handleNavBack={onCancelEdit}
        autoHeight={true}
        isHideBackdrop={true}
        onConfirm={handleOpenAuthPesel}
        confirmButtonLabel="Zapisz"
        confirmButtonDisabled={isUpdateButtonDisabled}
        buttonsContainerStyle={{ marginTop: '20px' }}
        annotations={
          <>
            <Grid item xs={12}>
              <hr className={classes.line}></hr>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.footer}>{passwordPolicy && getPasswordPolicyMessage(passwordPolicy)}</div>
            </Grid>
          </>
        }
      >
        <Grid container className={classes.container} style={{ paddingTop: '60px' }}>
          <Grid>
            <div className={classes.modalSubTitle}>Ustaw hasło w {t('global:shortNames.system')}</div>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: invalidPassword ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="new-password"
              >
                Hasło
              </InputLabel>
              <OutlinedInput
                id="new-password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleChangeNewPassword}
                fullWidth={true}
                error={invalidPassword}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton disableRipple aria-label="włącz widoczność hasła" onClick={handleClickShowNewPassword} edge="end">
                      {!showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: invalidPassword ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Hasło"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{
                  color: invalidPassword ? '#BC0000' : '#2C2D3E',
                }}
                htmlFor="repeted-password"
              >
                Powtórz nowe hasło
              </InputLabel>
              <OutlinedInput
                id="repeted-password"
                type={showRepeatedPassword ? 'text' : 'password'}
                value={repeatedPassword}
                onChange={handleChangeRepeatedPassword}
                fullWidth={true}
                error={invalidPassword}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowRepeatedPassword}
                      edge="end"
                    >
                      {!showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: invalidPassword ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Powtórz nowe hasło"
              />
              <FormHelperText className={classes.errorMessagesContainer} error id="password-error" component={'div'}>
                {errorCodes.includes(FrontendResponseErrorCode.PasswordsNotTheSame) ? (
                  <div className={classes.errorMessage}>
                    {getErrorMessages(FrontendResponseErrorCode.PasswordsNotTheSame)}
                  </div>
                ) : (
                  <>
                    {!errorCodes.includes(BackendErrorCode.PasswordMismatch) &&
                      errorCodes.map((code: ErrorCode) => (
                        <div className={classes.errorMessage} key={code}>
                          {getErrorMessages(code)}
                        </div>
                      ))}
                  </>
                )}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </ModalWithButtons>
      <AuthUserPesel
        open={openAuthUserPesel}
        password={newPassword}
        token={props.token}
        email={props.email}
        onConfirm={handleConfirmAccount}
        onClose={handleCloseAuthPesel}
      />
    </>
  );
};
